/**
 * Chronic Medication Model
 * Represents chronic medication records from Salesforce
 * Matches ChronicMedicationDetailDto from API controller
 */

export interface ChronicMedication {
  id: string;
  name?: string; // Name field (medication number)
  account?: string; // Account__c (patient ID)
  drug?: string; // Drug__c (drug ID)
  cnp?: string; // CNP__c
  drug_Name?: string; // Drug_Name__c
  frequency?: string; // Frequency__c
  observations?: string; // Observations__c
  quantity?: string; // Quantity__c
  data_de_Inicio_de_Toma?: string; // Data_de_Inicio_de_Toma__c
  data_de_Fim_de_Toma?: string; // Data_de_Fim_de_Toma__c
  contact?: string; // Contact__c
  contact_name?: string; // Contact_Name__c
  nome_Utente?: string; // Nome_Utente (from Account navigation - may be SF ID)
  patientName?: string; // Resolved patient name from Account navigation
  observa_es_da_medica_o?: string; // Observa_es_da_medica_o__c
  hora?: string; // Hora__c
  userLookup?: string; // UserLookup__c
  createdById?: string; // CreatedById
  lastModifiedById?: string; // LastModifiedById
  createdDate?: string; // CreatedDate
  lastModifiedDate?: string; // LastModifiedDate
  comprimidos_Consumidos?: number; // Comprimidos_Consumidos__c

  // MAT tracking fields
  mat?: boolean; // Mat__c - MAT Ativo
  lastSalesProcessedDate?: string; // LastSalesProcessedDate__c (sales cursor — only moves on a purchase)
  lastCalculationDate?: string; // LastCalculationDate__c (consumption cursor — as-of date of remainingDays; use for staleness/projection)
  matDate?: string; // MatDate__c - date MAT was last activated
  lastStockMg?: number; // LastStockMg__c
  lastStockPills?: number; // LastStockPills__c
  remainingDays?: number; // RemainingDays__c
  posologyRecomputePending?: boolean; // PosologyRecomputePending__c — RemainingDays may be stale until next daily run
  medicationStatus?: string; // MedicationStatus__c
  matStatus?: string; // MatStatus__c
  matStatusTooltip?: string;
  stockAdjustments?: StockAdjustmentRecord[];
  remainingDaysHistory?: RemainingDaysHistoryRecord[];

  // Navigation properties from API
  drugName?: string; // DrugNavigation.Name
  drugCNP?: string; // DrugNavigation.CNP
  drugPOPId?: string; // DrugNavigation.POP_ID
  drugDuracaoPlanoDeTratamento?: string; // DrugNavigation.Duracao_do_Plano_de_Tratamento
  dci?: string; // DCI - Denominação Comum Internacional (from Drug.Nome_Internacional__c)
  dosagem?: string; // Dosagem (from Drug)
  drugGrupoHomogeneo?: string; // Drug.Grupo_Homogeneo__c
  drugFormaFarmaceutica?: string; // Drug.Forma_Farmaceutica__c
  drugCNPEM?: string; // Drug.CNPEM__c
  drugTitularAim?: string; // Drug.Titular_Aim__c
  drugClassDisp?: string; // Drug.Class_Disp__c
  drugClassEstup?: string; // Drug.Class_Estup__c
  drugATC?: string; // Drug.ATC__c
  drugMatEligible?: boolean; // Drug.Mateligible__c — gates the MAT toggle on the detail surface
  
  // Legacy/computed properties for backward compatibility
  medicationNumber?: string; // Alias for name
  drugNameField?: string; // Alias for drug_Name
  accountId?: string; // Alias for account
  idCliente?: number | null; // IdCliente__c from Account navigation
  contactId?: string; // Alias for contact
  
  // Computed by backend: lastCalculationDate + remainingDays
  nextPurchaseDate?: string;

  // Picklist mappings from API response
  picklistMappings?: ChronicMedicationPicklistMappings;
}

export interface ChronicMedicationSearchCriteria {
  accountId?: string;
  medicationNumber?: string;
  drugName?: string;
  limit?: number;
  skip?: number;
  orderBy?: string;
}

export interface ChronicMedicationCreateRequest {
  drugId: string; // Drug__c (ID, not name)
  accountId: string; // Account__c (required)
  contact?: string; // Contact__c (optional)
  data_de_Inicio_de_Toma?: string; // Data_de_Inicio_de_Toma__c
  data_de_Fim_de_Toma?: string; // Data_de_Fim_de_Toma__c
  observa_es_da_medica_o?: string; // Observa_es_da_medica_o__c
  hora?: string; // Hora__c
  observations?: string; // Observations__c
  quantity?: string; // Quantity__c (label value, not key)
  frequency?: string; // Frequency__c (label value, not key)
  mat?: boolean; // Mat__c
  medicationStatus?: string; // MedicationStatus__c
  matStatus?: string; // MatStatus__c
  idCliente?: string; // IdCliente__c — only sent when manually entered
  lastSalesProcessedDate?: string; // LastSalesProcessedDate__c — set to now when MAT=true
  lastStockPills?: number; // LastStockPills__c — initial stock in pills (triggers stock adjustment)
}

export interface ChronicMedicationUpdateRequest {
  id: string;
  data_de_Inicio_de_Toma?: string;
  data_de_Fim_de_Toma?: string;
  observa_es_da_medica_o?: string;
  hora?: string;
  observations?: string;
  quantity?: string;
  frequency?: string;
  contact?: string;
  mat?: boolean;
  medicationStatus?: string; // MedicationStatus__c
  matStatus?: string; // MatStatus__c
  idCliente?: string;
  accountId?: string;
  // Controller-only flag (NOT a SF field). Honored only when mat is being flipped false → true.
  // true = wipe LastStockMg/LastStockPills on the medication (começar do zero).
  resetStock?: boolean;
}

// MAT deactivation ────────────────────────────────────────────────────────

export type MatDeactivationReasonValue =
  | 'Tratamento_concluido'
  | 'Suspensao_terapeutica'
  | 'Substituicao_terapeutica'
  | 'Utente_deixou_de_tomar'
  | 'Nao_cumpre_criterios_mat'
  | 'Caso_encerrado_farmacia'
  | 'Sem_acompanhamento_possivel'
  | 'Erro_no_registo'
  | 'A_pedido_do_utente'
  | 'Outro';

export interface MatDeactivationCreateRequest {
  reason: MatDeactivationReasonValue;
  reasonOther?: string;
  /** The team member who deactivated it (Team_Member__c); the pharmacy when absent. */
  teamMemberId?: string;
}

export interface MatDeactivationResponse {
  deactivationLogId: string;
  chronicMedicationId: string;
  mat: boolean;
}

export interface MatDeactivationLog {
  id: string;
  chronicMedicationId: string;
  reason: MatDeactivationReasonValue;
  reasonOther?: string | null;
  lastStockMgSnapshot?: number | null;
  lastStockPillsSnapshot?: number | null;
  lastSalesProcessedDateSnapshot?: string | null;
  deactivatedById?: string | null;
  deactivatedByName?: string | null;
  createdDate: string;
}

export interface ChronicMedicationPicklistMappings {
  Frequency?: Record<string, string>;
  Quantity?: Record<string, string>;
  Contact__c?: Record<string, string>;
}

// MedCompliance sales history ─────────────────────────────────────────────

export interface MedicationSalesItem {
  dci: string;
  drugName?: string | null;
  drugId?: string | null;
  cnp?: string | null;
  dosagem?: string | null;
  // Backend may return the last posology under different keys: "ultimaPosologia" or "posologia".
  ultimaPosologia?: string | null;
  posologia?: string | null;
  totalSales: number;
  lastSaleDate?: string | null;
  alreadyExistsInTherapy: boolean;
  // MAT-eligible flag of the matched Drug (null when the sold product isn't in our catalogue).
  mateligible?: boolean | null;
}

export interface MedicationSalesHistoryResponse {
  hasIdCliente: boolean;
  idCliente?: number | null;
  hasSales: boolean;
  /** Customer not found but sales data is fresh — likely a wrong client number. */
  patientNotFound: boolean;
  /** Pharmacy has not authorized sales-data sharing (Farmacia missing/OUT or AdendaGlintt false). */
  pharmacyNotAuthorized?: boolean;
  /** Customer not found and the pharmacy's sales data is stale or never synced. */
  salesDataStale?: boolean;
  /** Most recent sales-sync date for the pharmacy; null when never synced. */
  lastSalesSyncDate?: string | null;
  sales: MedicationSalesItem[];
  emptyMessage?: string | null;
}

export interface PagedResponse<T> {
  items: T[];
  total: number;
}

export interface StockAdjustmentRecord {
  id: string;
  name?: string;
  createdDate?: string;
  type?: string;       // Type__c picklist (Entrada/Saída)
  quantity?: number;   // Quantity__c
  motive?: string;     // Motive__c picklist
  teamMemberName?: string | null;
}

export interface RemainingDaysHistoryRecord {
  id: string;
  name?: string;
  createdDate?: string;
  remainingDays?: number;             // RemainingDays__c
  date?: string;                      // Date__c
  adherenceReported?: string | null;  // AdherenceReported__c
  nonAdherenceReason?: string | null; // Non_Adherence_Reason__c
  nonAdherenceReasonOther?: string | null; // NonAdherenceReasonOther__c
  /** Server-computed: the stored SnoozedUntil date when adherenceReported = 'Adiado', else null.
   *  Drives the "Adiado até DD/MM" badge (4-mat-snooze-tratado-adiado). (JSON name kept as
   *  `snoozedUntil` until the backend contract rename lands.) */
  snoozedUntil?: string | null;
}

export interface AdherenceHistoryRecord {
  id: string;
  chronicMedicationId: string;
  drug?: string | null;                    // populated only by patient-level endpoint
  date?: string | null;                    // Date__c — the pharmacy day reported
  adherenceReported: string;               // one of AdherenceReportedValue
  nonAdherenceReason?: string | null;
  nonAdherenceReasonOther?: string | null;
  /** For Adiado rows: stored date the snooze applies through (SnoozedUntil__c); null otherwise. */
  snoozedUntil?: string | null;
  /** RemainingDays__c for the row; null when no daily-snapshot value exists. Plotted by the chart. */
  remainingDays?: number | null;
  /** What produced the row; drives the chart's event markers. Defaults to 'adesao' server-side. */
  eventType?: AdherenceEventType;
  recordedDate: string;                    // LastModifiedDate of the row
  recordedById?: string | null;
  recordedByName?: string | null;
}

/** Classifies what produced an adherence-history row (mirror of the backend
 *  `MatAdherenceEventType`). Drives the remaining-days chart's event markers. */
export type AdherenceEventType = 'venda' | 'acerto' | 'posologia' | 'adesao';

/** Capped adherence-history response. `hasMore` is true when older rows exist
 *  beyond the returned cap, so the UI can flag that the list is truncated. */
export interface AdherenceHistoryPage {
  rows: AdherenceHistoryRecord[];
  hasMore: boolean;
  total: number;
}

/** Hard cap on rows returned by the adherence-history endpoints. Mirror of the
 *  backend `MedicationRemainingDaysHistoryRepository.AdherenceHistoryCap`
 *  constant; used by the truncation-note copy on the detail surfaces. */
export const ADHERENCE_HISTORY_CAP = 100;

/** Normalize the adherence-history HTTP response into the page shape. Tolerates
 *  the legacy bare-array body so an out-of-sync backend cannot poison the signal
 *  with undefined, and the null branch keeps a 204/204-like body harmless. */
export function normalizeAdherenceHistoryPage(res: unknown): AdherenceHistoryPage {
  if (Array.isArray(res)) return { rows: res as AdherenceHistoryRecord[], hasMore: false, total: res.length };
  const page = res as Partial<AdherenceHistoryPage> | null | undefined;
  return { rows: page?.rows ?? [], hasMore: page?.hasMore ?? false, total: page?.total ?? (page?.rows?.length ?? 0) };
}

export type AdherenceReportedValue =
  | 'Ok'
  | 'Toma_mas_nao_corretamente'
  | 'Comecou_e_suspendeu'
  | 'Nao_comecou'
  // 4-mat-snooze-tratado-adiado: snooze values stored on the same picklist but written
  // by the dedicated defer endpoints (POST/DELETE /chronicmedication/{id}/defer + the bulk
  // mat-defer endpoint), NOT by the adherence-note path. The "Tratado"/"Adiar" values are
  // removed (the "Tratado" concept no longer exists).
  | 'Adiado'
  | 'Cancelado';

/** Subset of AdherenceReportedValue accepted by the clinical adherence-note
 *  modal + endpoint. The snooze values (Adiado/Cancelado) are excluded here so
 *  the clinical flow cannot accidentally type a snooze payload. */
export type ClinicalAdherenceReportedValue = Exclude<AdherenceReportedValue, 'Adiado' | 'Cancelado'>;

/** Adiado duration bounds (days), mirrored from the backend defer endpoints. */
export const DEFER_MIN_DAYS = 1;
export const DEFER_MAX_DAYS = 30;
export const DEFER_DEFAULT_DAYS = 15;

export type NonAdherenceReasonValue =
  | 'Falha_nao_intencional'
  | 'Complexidade_do_tratamento'
  | 'Baixa_compreensao_necessidade'
  | 'Percecao_baixa_efetividade'
  | 'Tolerabilidade_medicamento'
  | 'Limitacoes_clinicas_funcionais'
  | 'Barreiras_economicas_acesso'
  | 'Outro';

export interface AdherenceNoteRequest {
  /** Clinical adherence-note path accepts only the clinical subset.
   *  Adiado/Cancelado are rejected here with 422 by the API; use the
   *  dedicated /defer endpoints (see DeferRequest / MatDeferRequest). */
  adherence_reported: ClinicalAdherenceReportedValue;
  non_adherence_reason?: NonAdherenceReasonValue | null;
  non_adherence_reason_other?: string | null;
  /** The team member who recorded the note (Team_Member__c); the pharmacy when absent. */
  teamMemberId?: string;
}

/** Payload for the bulk patient-level defer endpoint (POST /Patients/{id}/mat-defer,
 *  4-mat-snooze-tratado-adiado). Postpones ("Adiar") the listed medications for `days`. */
export interface MatDeferRequest {
  chronicMedicationIds: string[];
  days: number;
  note?: string;
  teamMemberId?: string;
}

/** Payload for the single-medication defer sugar endpoint
 *  (POST /chronicmedication/{id}/defer). */
export interface DeferRequest {
  days: number;
  note?: string;
}

/** Response envelope for the bulk defer endpoint. */
export interface MatDeferResponse {
  affectedMedicationIds: string[];
  rows: RemainingDaysHistoryRecord[];
}
